/**
 * App-level subscription to `/api/v1/platform/events`.
 *
 * The SSE connection lives above the router so it survives navigation
 * between dashboard / compute / chat. A subscription scoped to a single
 * route would miss every event fired while the user is on another page.
 *
 * Reconnect on auth changes: when a fresh access token lands (e.g. after
 * the 30s-before-expiry refresh in AuthProvider), this provider opens a
 * new stream so the broadcast receiver inside the BE picks up future
 * events with the up-to-date credentials.
 */
import {
  type Component,
  type JSX,
  createContext,
  createEffect,
  createSignal,
  onCleanup,
  useContext,
} from "solid-js";

import {
  ACTIVITY_BUFFER_LIMIT,
  type ActivityRow,
  appendActivity,
  fetchCliActivityHistory,
  mergeHistory,
  streamPlatformEvents,
} from "@/api/platform";
import { useAuth } from "@/auth/AuthProvider";

interface ActivityContextValue {
  rows: () => ActivityRow[];
  streamLive: () => boolean;
}

const ActivityContext = createContext<ActivityContextValue>();

export const ActivityProvider: Component<{ children: JSX.Element }> = (
  props,
) => {
  const auth = useAuth();
  const [rows, setRows] = createSignal<ActivityRow[]>([]);
  const [streamLive, setStreamLive] = createSignal(false);

  createEffect(() => {
    if (!auth.isAuthenticated()) {
      setStreamLive(false);
      return;
    }
    const ctl = new AbortController();
    onCleanup(() => ctl.abort());
    // Seed the feed with recent CLI activity so it isn't empty on load (the
    // SSE stream below only carries events fired while subscribed). Best-effort
    // and concurrent with the stream; mergeHistory dedups any overlap.
    (async () => {
      try {
        const history = await fetchCliActivityHistory(
          ACTIVITY_BUFFER_LIMIT,
          ctl.signal,
        );
        setRows((buf) => mergeHistory(buf, history));
      } catch {
        // History is best-effort — the live stream still works without it.
      }
    })();
    (async () => {
      try {
        for await (const ev of streamPlatformEvents({ signal: ctl.signal })) {
          setStreamLive(true);
          setRows((buf) => appendActivity(buf, ev));
        }
      } catch {
        // Stream closed (abort or network drop). Leave streamLive at
        // its last value; the next auth tick will reopen.
      } finally {
        setStreamLive(false);
      }
    })();
  });

  return (
    <ActivityContext.Provider value={{ rows, streamLive }}>
      {props.children}
    </ActivityContext.Provider>
  );
};

export function useActivity(): ActivityContextValue {
  const ctx = useContext(ActivityContext);
  if (!ctx) throw new Error("useActivity must be inside <ActivityProvider>");
  return ctx;
}
